import {
  Badge,
  Button,
  Label,
  Switch,
  Textarea,
  confirm,
} from "@devhop/ui";
import { RotateCcw, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { useResetTemplate, useUpsertTemplate } from "../api/queries";
import type {
  MessageTemplateRow,
  TemplateKind,
  TemplateVariableInfo,
} from "../api/types";
import { TemplatePreviewPane } from "./TemplatePreviewPane";
import { VariableChipList } from "./VariableChipList";

type Props = {
  pageId: string;
  template: MessageTemplateRow;
  variables: TemplateVariableInfo[];
  title: string;
  description?: string;
};

export function TemplateEditorCard({
  pageId,
  template,
  variables,
  title,
  description,
}: Props) {
  const [content, setContent] = useState(template.content);
  const [isActive, setIsActive] = useState(template.isActive);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    setContent(template.content);
    setIsActive(template.isActive);
  }, [template.content, template.isActive, template.kind, template.pageId]);

  const upsert = useUpsertTemplate(pageId);
  const reset = useResetTemplate(pageId);

  const dirty =
    content !== template.content || isActive !== template.isActive;

  const handleInsert = (key: string) => {
    const ta = textareaRef.current;
    const token = `{{${key}}}`;
    if (!ta) {
      setContent((prev) => prev + token);
      return;
    }
    const start = ta.selectionStart ?? content.length;
    const end = ta.selectionEnd ?? content.length;
    const next = content.slice(0, start) + token + content.slice(end);
    setContent(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const handleSave = () => {
    upsert.mutate({
      kind: template.kind as TemplateKind,
      content,
      isActive,
    });
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: "ກັບເປັນຄ່າເລີ່ມຕົ້ນ?",
      description:
        "ຂໍ້ຄວາມທີ່ແກ້ໄຂຈະຖືກລົບ ແລ້ວໃຊ້ template ມາດຕະຖານແທນ",
      actionText: "ກັບເປັນຄ່າເລີ່ມຕົ້ນ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) reset.mutate(template.kind as TemplateKind);
  };

  const updatedLabel = template.updatedAt
    ? formatDateTimeLocal(template.updatedAt)
    : "-";

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex flex-col gap-4 border-b p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-medium text-base">{title}</h2>
            <Badge variant={template.isDefault ? "secondary" : "outline"}>
              {template.isDefault ? "ຄ່າເລີ່ມຕົ້ນ" : "ກຳນົດເອງ"}
            </Badge>
          </div>
          {description ? (
            <p className="text-muted-foreground text-sm">{description}</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-3 sm:justify-end">
          <div className="flex items-center gap-2">
            <Label
              htmlFor={`active-${template.kind}`}
              className="text-muted-foreground text-xs"
            >
              ເປີດໃຊ້ງານ
            </Label>
            <Switch
              id={`active-${template.kind}`}
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void handleReset()}
              disabled={template.isDefault || reset.isPending}
            >
              <span className="inline-flex items-center gap-2">
                <RotateCcw className="size-4" aria-hidden />
                ກັບເປັນຄ່າເລີ່ມຕົ້ນ
              </span>
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!dirty || upsert.isPending}
            >
              <span className="inline-flex items-center gap-2">
                <Save className="size-4" aria-hidden />
                ບັນທຶກ
              </span>
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <VariableChipList
          variables={variables}
          kind={template.kind as TemplateKind}
          onInsert={handleInsert}
          layout="horizontal"
        />

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-2">
            <Label className="text-muted-foreground text-xs">ຂໍ້ຄວາມ template</Label>
            <Textarea
              ref={textareaRef}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={16}
              className="min-h-[280px] font-mono text-sm"
            />
            <div className="flex flex-wrap items-center justify-between gap-2 text-muted-foreground text-xs">
              <span>
                {template.isDefault
                  ? "ກຳລັງໃຊ້ template ມາດຕະຖານ"
                  : `ແກ້ໄຂລ່າສຸດ: ${updatedLabel}`}
              </span>
              <span className="tabular-nums">{content.length} ຕົວອັກສອນ</span>
            </div>
          </div>

          <div className="lg:sticky lg:top-4 lg:self-start">
            <TemplatePreviewPane
              kind={template.kind as TemplateKind}
              content={content}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
